import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography } from '@mui/material';
import { UserEditForm } from '../forms/UserEditForm';
import { OrganizerEditForm } from '../forms/OrganizerEditForm';
import { GrowerEditForm } from '../forms/GrowerEditForm';
import { MaterialEditForm } from '../forms/MaterialEditForm';
import { useFormWithSchema } from '../../../../src/utils/functions/yupFormTypescript';
import { CustomTab } from '../../../../src/components/customTab';
import { TDiscountTab } from '../../../../src/utils/types';

import { Table } from '../../../../src/components';
import { GeographyEditForm } from '../forms/GeographyEditForm';
import { useAddOrUpdateDataByTableTypeMutation } from '../../../../src/services/mVPGraphQlService';
import { useDispatch, useSelector } from 'react-redux';
import { showAlert } from '../../../../src/services/alert';
import { RootState } from '../../../../src/store';
import dayjs from 'dayjs';
import arrow from '../../../assets/icons/line_arrow-close-left.svg';

type ImportUsersDrawerProps = {
  open: boolean;
  onClose: () => void;
  type: string;
  schema: any;
  data: any;
  rowIndex: number | null;
  auditData: any;
  refetch: any;
  initialTab: 'details' | 'audit';
  commonFilters: any;
  isActive?: boolean;
};

export default function EditDrawer({
  open,
  onClose,
  type,
  schema,
  data,
  rowIndex,
  auditData,
  refetch,
  initialTab,
  commonFilters,
  isActive = true,
}: ImportUsersDrawerProps) {
  const {
    formState: { errors, isDirty },
    handleSubmit,
    register,
    control,
    reset,
    watch,
  } = useFormWithSchema(schema, {
    mode: 'onTouched',
  });

  const parseValue = (value?: string) => {
    try {
      return value ? JSON.parse(value) : {};
    } catch {
      return {};
    }
  };

  const makeAuditHistory = (auditRecords = []) => {
    const sorted = [...auditRecords].sort(
      (a, b) =>
        a.updatedActorTrace?.actorTime?.seconds -
        b.updatedActorTrace?.actorTime?.seconds
    );

    let previousSnapshot = {};

    return sorted.flatMap((record) => {
      const currentSnapshot = parseValue(
        record.updatedValue
      );

      const rows = Object.keys(currentSnapshot)
        .map((key) => {
          if (
            previousSnapshot[key] === currentSnapshot[key]
          )
            return null;

          return {
            attribute: key,
            previousValue: previousSnapshot[key] ?? '---',
            updatedValue: currentSnapshot[key] ?? '---',
            updatedBy:
              record.updatedActorTrace?.actorId
                ?.actorName || '-',
            updatedOn: dayjs(
              record.updatedActorTrace?.actorTime?.seconds
                ? record.updatedActorTrace?.actorTime
                    ?.seconds * 1000
                : 0
            ).format('DD/MM/YYYY, hh:mm A'),
          };
        })
        .filter(Boolean);

      previousSnapshot = {
        ...previousSnapshot,
        ...currentSnapshot,
      };

      return rows;
    });
  };

  useEffect(() => {
    if (
      open &&
      rowIndex !== null &&
      rowIndex !== undefined &&
      data?.[rowIndex]
    ) {
      reset(data[rowIndex]);
    }
  }, [open, rowIndex, data, reset]);
  const [addorUpdateRequest] =
    useAddOrUpdateDataByTableTypeMutation();
  const dispatch = useDispatch();

  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );
  const countryName = useSelector(
    (state: RootState) => state.country.countryName
  );
  const countryCode = useSelector(
    (state: RootState) => state.country.countryCode
  );

  const onSubmit = (data) => {
    const isUser = type === 'user';

    const userRecord = {
      userDetails: {
        name: data.name,
        userId: data?.userId,
        ugdn: data.ugdn,
        mobileDetails: {
          mobileNumber: data.mobileDetails,
          countryCode: countryCode,
        },
        ...(countryName === 'Thailand' && {
          emailId: data.emailId,
        }),
        country: countryName,
        userType: data.userType,
        userFunction: data.userFunction,
        userSubFunction: data.userSubFunction,
        status: true,
      },
      peopleId: {
        regionManagerId: data.regionManagerId,
        tpmId: data.tpmId,
        faId: data.faId,
        scmId: data.scmId,
        plId: data.plId,
      },
    };

    const geoRecord = {
      geoId: data?.geoId,
      regionId: data?.regionId,
      subRegionId: data?.subRegionId,
      villageId: data.villageId,
      region: data.region,
      subRegion: data.subRegion,
      village: data.village,
      subRegionCode: data.subRegionCode,
      country: countryName,
      ...(countryName !== 'India' && {
        cluster: data.cluster,
        clusterId: data.clusterId,
      }),
    };

    const obj = {
      dataType: 'DT_ROW',
      tableType: isUser ? 'TT_USER' : 'TT_GEOGRAPHY',
      wsId: wsId,
      dataPayload: {
        ...(isUser && { userRecord }),
        ...(!isUser && { geoRecord }),
      },
    };

    addorUpdateRequest(obj)
      .unwrap()
      .then((response) => {
        const inspectionData =
          response?.data?.AddOrUpdateDataByTableType;
        if (
          inspectionData?.responseStatus?.status ===
          'SUCCESS'
        ) {
          onClose();
          dispatch(
            showAlert({
              content: 'Edited successfully',
            })
          );
          refetch();
        } else {
          dispatch(
            showAlert({
              content: 'Something went wrong',
              severity: 'error',
            })
          );
        }
      })

      .catch((err) => {
        console.error('Error:', err);
        dispatch(
          showAlert({
            content: 'something went wrong',
            severity: 'error',
          })
        );
      });
  };

  const DISCOUNT_TABS: TDiscountTab[] = [
    ...(isActive
      ? [
          {
            label: 'Details',
            tabIndex: '1',
            fragment: 'details',
          },
        ]
      : []),
    ...(type !== 'geography'
      ? [
          {
            label: 'Audit history',
            tabIndex: '2',
            fragment: 'audit-history',
          },
        ]
      : []),
  ];

  const tabFromIntent =
    initialTab === 'audit'
      ? DISCOUNT_TABS.find(
          (t) => t.fragment === 'audit-history'
        )
      : DISCOUNT_TABS.find((t) => t.fragment === 'details');

  const [activeTab, setActiveTab] = useState(
    tabFromIntent || DISCOUNT_TABS[0]
  );
  useEffect(() => {
    if (open) {
      setActiveTab(
        initialTab === 'audit'
          ? DISCOUNT_TABS.find(
              (t) => t.fragment === 'audit-history'
            )!
          : DISCOUNT_TABS.find(
              (t) => t.fragment === 'details'
            )!
      );
    }
  }, [open, initialTab]);

  const handleChange = (newSelectedTab: TDiscountTab) => {
    if (newSelectedTab) {
      setActiveTab(newSelectedTab);
    }
  };
  const headers = [
    'attribute',
    'previousValue',
    'updatedBy',
    'updatedOn',
    'updatedValue',
  ];

  const columnsss = [
    ...headers.map((item) => ({
      key: item,
      label: item,
    })),
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '50vw',
          backgroundColor: '#fff',
        },
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid #E0E0E0',
          boxShadow: '0px 2px 6px rgba(0,0,0,0.04)',
        }}>
        <Typography
          fontSize={17}
          fontWeight={600}
          sx={{ lineHeight: 1 }}>
          {type === 'user'
            ? 'Edit User'
            : type === 'geography'
            ? 'Edit Geography'
            : type === 'organizer'
            ? 'Edit Organizer'
            : type === 'grower'
            ? 'Edit Grower'
            : type === 'field'
            ? 'Edit Field'
            : 'Edit Material'}
        </Typography>

        <Box
          onClick={onClose}
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}>
          <img
            src={arrow}
            alt="close"
            style={{ width: 20, height: 20 }}
          />
        </Box>
      </Box>

      <CustomTab<TDiscountTab, keyof TDiscountTab>
        tabList={DISCOUNT_TABS}
        activeTab={activeTab}
        valueKey={'tabIndex'}
        labelKey={'label'}
        onTabChange={handleChange}
        fontsize={13}
        tabPanel={(tab) => {
          if (
            type === 'geography' &&
            tab?.tabIndex === '2'
          ) {
            return null;
          }

          switch (tab?.tabIndex) {
            case '1':
              return (
                <Box>
                  {type === 'user' && (
                    <UserEditForm
                      register={register}
                      errors={errors}
                      control={control}
                      onSubmit={onSubmit}
                      handleClose={onClose}
                      handleSubmit={handleSubmit}
                      //  role={role}
                      watch={watch}
                      commonFilters={commonFilters}
                      isDirty={isDirty}
                    />
                  )}
                  {type === 'geography' && (
                    <GeographyEditForm
                      register={register}
                      errors={errors}
                      control={control}
                      onSubmit={onSubmit}
                      handleClose={onClose}
                      handleSubmit={handleSubmit}
                      isDirty={isDirty}
                    />
                  )}
                  {type === 'organizer' && (
                    <OrganizerEditForm
                      control={control}
                      register={register}
                      errors={errors}
                      onSubmit={onSubmit}
                      handleClose={onClose}
                      handleSubmit={handleSubmit}
                    />
                  )}
                  {type === 'grower' && (
                    <GrowerEditForm
                      control={control}
                      register={register}
                      errors={errors}
                      onSubmit={onSubmit}
                      handleClose={onClose}
                      handleSubmit={handleSubmit}
                    />
                  )}
                  {/* {type === 'field' && (
                      <FieldForm
                        register={register}
                        errors={errors}
                      />
                   )}*/}
                  {type === 'material' && (
                    <MaterialEditForm
                      control={control}
                      register={register}
                      errors={errors}
                      onSubmit={onSubmit}
                      handleClose={onClose}
                      handleSubmit={handleSubmit}
                    />
                  )}
                </Box>
              );

            case '2':
              return (
                <Table
                  columns={columnsss}
                  data={makeAuditHistory(auditData)} //add page size and all
                  rowsPerPageOptions={[10]}></Table>
              );
          }
        }}
      />
    </Drawer>
  );
}
